import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useGetVehicleLoanApplicationQuery } from "@lending/hooks/queries/VehicleLoanApplication/useGetVehicleLoanApplicationQuery";
import { useLendingApplicationID } from "@lending/hooks/useLendingApplicationID";

interface AdditionalField {
  name: string;
  value: boolean;
}

interface AutoLoanApplicationShape {
  has_vehicle_selected?: boolean;
  is_refinance?: boolean;
  requested_amount_cents?: number | null;
}

function isAutoRequestedAmountEnabled(additionalFields: AdditionalField[] | undefined) {
  return (
    additionalFields?.some((field) => field.name === "auto_requested_loan_amount" && field.value) ??
    false
  );
}

function shouldCollectAutoRequestedAmount(
  additionalFields: AdditionalField[] | undefined,
  application: AutoLoanApplicationShape | undefined
) {
  return (
    isAutoRequestedAmountEnabled(additionalFields) &&
    application?.has_vehicle_selected === false &&
    application?.is_refinance === false &&
    application?.requested_amount_cents == null
  );
}

function useAutoLoanFlowState(enabled = true) {
  const applicationUuid = useLendingApplicationID();
  const { institution } = useGetInstitutionQuery();
  const vehicleLoanApplicationQuery = useGetVehicleLoanApplicationQuery({
    enabled: enabled && Boolean(applicationUuid),
  });

  const additionalFields = institution.lending_ask_additional_fields;
  const application = vehicleLoanApplicationQuery.data;
  const autoRequestedAmountEnabled = isAutoRequestedAmountEnabled(additionalFields);

  return {
    isLoading: vehicleLoanApplicationQuery.isLoading,
    isError: vehicleLoanApplicationQuery.isError,
    application,
    hasVehicleSelected: application?.has_vehicle_selected,
    isRefinance: application?.is_refinance,
    isAutoRequestedAmountEnabled: autoRequestedAmountEnabled,
    shouldCollectAutoRequestedAmount: shouldCollectAutoRequestedAmount(
      additionalFields,
      application
    ),
  };
}

export { useAutoLoanFlowState };
